import { Box, Link, Menu, MenuItem, Typography } from '@mui/material'
import { usePopupState, bindTrigger, bindMenu } from 'material-ui-popup-state/hooks'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import { useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { LinkRouter } from 'components/breadcrumbs'

type IPopupMenu = {
  label: string
  items: {
    label: string
    url: string
    disabled?: boolean
  }[]
  showOnAllPermission?: boolean
  showInList?: boolean
}

export function PopupMenu({ label, items, showOnAllPermission, showInList = false }: IPopupMenu) {
  const popupState = usePopupState({ variant: 'popover', popupId: 'popup-menu' })
  const navigate = useNavigate()

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      popupState.close()
    },
    [popupState]
  )

  const handleItemClick = (event: React.MouseEvent<HTMLAnchorElement>, url: string) => {
    // If modifier keys are pressed, let the browser handle it (e.g. open in new tab)
    if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey || event.button === 1) {
      popupState.close()
      return
    }

    // Otherwise, prevent default and navigate manually after closing popup
    event.preventDefault()
    popupState.close()
    setTimeout(() => {
      navigate(url)
    }, 0)
  }

  const isAllDisabled = items.every((item) => item.disabled)

  if (showOnAllPermission && isAllDisabled) {
    return null
  }

  if (items.length === 1 && !showInList) {
    return (
      <LinkRouter
        underline={items[0].disabled ? 'none' : 'hover'}
        color="inherit"
        variant="body2DMSans"
        to={!items[0].disabled ? items[0].url : '#'}
        sx={{
          cursor: items[0].disabled ? 'not-allowed' : 'pointer',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <Typography
          variant="body2DMSans"
          fontSize={14}
          color={items[0].disabled ? '#adaeb3ff !important' : 'white'}
        >
          {label}
        </Typography>
      </LinkRouter>
    )
  }

  return (
    <>
      <Typography
        color="white"
        variant="body2DMSans"
        {...bindTrigger(popupState)}
        sx={{
          cursor: isAllDisabled ? 'not-allowed' : 'pointer',
          display: 'flex',
          alignItems: 'center',
          color: isAllDisabled ? '#adaeb3ff !important' : 'white',
        }}
      >
        {label}
        {!isAllDisabled && <KeyboardArrowDownIcon fontSize="small" />}
      </Typography>

      <Menu {...bindMenu(popupState)}>
        {!isAllDisabled &&
          items.map((item, index) => (
            <MenuItem
              key={index}
              disabled={item.disabled || isAllDisabled}
              sx={{ px: 2, minWidth: 150 }}
            >
              <Box display="flex" mt="2px">
                <Link
                  underline={item.disabled || isAllDisabled ? 'none' : 'hover'}
                  variant="body2DMSans"
                  component={LinkRouter as any}
                  to={!item.disabled ? item.url : '#'}
                  onClick={(e: React.MouseEvent<HTMLAnchorElement>) =>
                    !item.disabled && handleItemClick(e, item.url)
                  }
                  sx={{
                    cursor: item.disabled ? 'not-allowed' : 'pointer',
                    textDecoration: 'none',
                    color: item.disabled || isAllDisabled ? '#adaeb3ff' : '#1B2064',
                  }}
                >
                  {item.label}
                </Link>
              </Box>
            </MenuItem>
          ))}
      </Menu>
    </>
  )
}
